import { useCallback } from 'react';
import type { RovoContentBridgeSetupHandler } from '@atlassian/rovo-content-bridge-api/client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { isPreviewPanelAvailable } from '@confluence/preview-panel-api/entry-points/isPreviewPanelAvailable';
import { useOpenPreviewPanel } from '@confluence/preview-panel-api/entry-points/useOpenPreviewPanel';

import {
	OpenPreviewPanelCommand,
	IsPreviewPanelAvailableCommand,
} from './commands/preview-panel-commands';

export const usePreviewPanelHandler = (): RovoContentBridgeSetupHandler => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { open: openPreviewPanel } = useOpenPreviewPanel();

	const bindHandlersCallback: RovoContentBridgeSetupHandler = useCallback(
		(on) => {
			// Handle OpenPreviewPanelCommand
			on(OpenPreviewPanelCommand, (data) => {
				const { url, ari } = data;

				try {
					openPreviewPanel({ url, ari });

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'opened',
							actionSubject: 'previewPanel',
							actionSubjectId: 'open',
							source: 'whiteboards-afm-bridge',
							attributes: {
								ari,
							},
						},
					}).fire();
				} catch (error) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'failed',
							actionSubject: 'previewPanel',
							actionSubjectId: 'open',
							source: 'whiteboards-afm-bridge',
							attributes: {
								reason: 'open_preview_panel_error',
								ari,
								error: error instanceof Error ? error.message : 'unknown_error',
							},
						},
					}).fire();
				}
			});

			// Handle IsPreviewPanelAvailableCommand
			on(IsPreviewPanelAvailableCommand, (data) => {
				const { ari } = data;

				try {
					const available = isPreviewPanelAvailable({ ari });

					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'checked',
							actionSubject: 'previewPanel',
							actionSubjectId: 'availability',
							source: 'whiteboards-afm-bridge',
							attributes: {
								ari,
								isAvailable: available,
							},
						},
					}).fire();

					return available;
				} catch (error) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'failed',
							actionSubject: 'previewPanel',
							actionSubjectId: 'checkAvailability',
							source: 'whiteboards-afm-bridge',
							attributes: {
								reason: 'check_availability_error',
								ari,
								error: error instanceof Error ? error.message : 'unknown_error',
							},
						},
					}).fire();

					return false;
				}
			});
		},
		[createAnalyticsEvent, openPreviewPanel],
	);

	return bindHandlersCallback;
};
